import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { Sparkles, Crown, Gem, Building2, Infinity as InfinityIcon, Loader2, Settings2, ArrowUpRight, Check, Download, RefreshCw, ListChecks, ChevronDown, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const TIER_META = {
  admin: { label: 'Admin', color: '#0A0F1C', Icon: InfinityIcon },
  agency: { label: 'Agency', color: '#0F766E', Icon: Building2 },
  premium: { label: 'Premium', color: '#0EA5E9', Icon: Gem },
  pro: { label: 'Pro', color: '#7C3AED', Icon: Crown },
  starter: { label: 'Starter', color: '#1D4ED8', Icon: Sparkles },
};

function QuotaChip({ label, remaining, cap }) {
  const out = remaining <= 0;
  return (
    <span
      data-testid={`studio-quota-${label.toLowerCase()}`}
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${out ? 'bg-rose-50 text-rose-600' : 'bg-emerald-50 text-emerald-700'}`}
    >
      {label}: {remaining}/{cap} left
    </span>
  );
}

export default function StudioPlanBar({ memberView = false }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const upgradeTargetRef = useRef('pro');

  const load = useCallback(async () => {
    try {
      const r = await api.get('/api/admin/social-studio/usage');
      setUsage(r.data);
      upgradeTargetRef.current = r.data?.upgrade_to || null;
    } catch {
      setUsage(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const startUpgrade = useCallback(async () => {
    const target = upgradeTargetRef.current;
    if (!target) return;
    setUpgrading(true);
    try {
      const r = await api.post('/api/social-studio/subscribe/checkout', { tier: target, origin_url: window.location.origin });
      if (r.data?.url) { window.location.href = r.data.url; return; }
      if (r.data?.changed) {
        toast.success(`You're now on the ${r.data.plan_name || target} plan.`);
        await load();
        return;
      }
      toast.error('Could not start checkout. Please try again.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upgrade failed. Please try again.');
    } finally {
      setUpgrading(false);
    }
  }, [load]);

  useEffect(() => {
    load();
    // Instant access on return from Stripe checkout: verify + grant immediately.
    try {
      const params = new URLSearchParams(window.location.search);
      const sid = params.get('session_id');
      const subscribed = params.get('subscribed');
      if (sid && subscribed) {
        api.post('/api/social-studio/checkout/verify', { session_id: sid })
          .then((r) => {
            if (r.data?.granted) toast.success(`You're all set! Your ${r.data.tier} plan is now active.`);
          })
          .catch(() => { /* webhook will still grant */ })
          .finally(() => {
            load();
            const clean = window.location.pathname;
            window.history.replaceState({}, '', clean);
          });
      }
    } catch (_e) { /* noop */ }
    const onRefresh = () => load();
    const onUpgrade = () => startUpgrade();
    window.addEventListener('studio-usage-refresh', onRefresh);
    window.addEventListener('studio-upgrade-request', onUpgrade);
    return () => {
      window.removeEventListener('studio-usage-refresh', onRefresh);
      window.removeEventListener('studio-upgrade-request', onUpgrade);
    };
  }, [load, startUpgrade]);

  if (loading || !usage) return null;

  const tier = usage.tier || 'starter';
  const meta = TIER_META[tier] || TIER_META.starter;
  const { Icon } = meta;
  const isAdmin = tier === 'admin';
  const upgradeTo = usage.upgrade_to;

  return (
    <div className="mb-5" data-testid="studio-plan-bar">
      <div
        className="flex flex-wrap items-center gap-3 rounded-2xl border px-4 py-3"
        style={{ borderColor: '#E9D5FF', background: 'linear-gradient(100deg, rgba(124,58,237,0.06), rgba(29,78,216,0.04))' }}
      >
        <span
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white text-xs font-black tracking-wide"
          style={{ background: meta.color }}
          data-testid="studio-tier-badge"
        >
          <Icon size={14} aria-hidden="true" /> {memberView && isAdmin ? 'Unlimited access' : `${meta.label} plan`}
        </span>

        {isAdmin ? (
          <span className="text-sm font-semibold text-[#0A0F1C]">Unlimited generations, no daily caps.</span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <QuotaChip label="Posts" remaining={usage.remaining?.posts ?? 0} cap={usage.limits?.posts ?? 0} />
            <QuotaChip label="Videos" remaining={usage.remaining?.videos ?? 0} cap={usage.limits?.videos ?? 0} />
            <span className="text-[11px] text-[#64748B]">
              Plans up to {usage.plan?.max_content_plan_days ?? 30} days, {usage.plan?.max_brand_profiles == null ? 'unlimited' : usage.plan.max_brand_profiles} brand{usage.plan?.max_brand_profiles === 1 ? '' : 's'}, resets daily
            </span>
          </div>
        )}

        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowPlanDetails((s) => !s)}
            data-testid="studio-plan-details-toggle"
            className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] hover:border-[#7C3AED] text-[#475569] text-xs font-bold px-3 py-2 rounded-full transition-colors"
          >
            <ListChecks size={14} /> {isAdmin ? "What's included" : 'My plan & limits'}
            <ChevronDown size={13} className={`transition-transform ${showPlanDetails ? 'rotate-180' : ''}`} />
          </button>
          {!isAdmin && (
            <Link
              to="/portal/studio/plans"
              className="text-xs font-bold text-[#7C3AED] hover:underline inline-flex items-center gap-1"
              data-testid="studio-compare-plans"
            >
              Compare plans <ArrowUpRight size={13} />
            </Link>
          )}
          {!isAdmin && upgradeTo && (
            <button
              type="button"
              onClick={startUpgrade}
              disabled={upgrading}
              data-testid="studio-upgrade-btn"
              className="inline-flex items-center gap-1.5 bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs font-bold px-4 py-2 rounded-full transition-colors disabled:opacity-60"
            >
              {upgrading ? <Loader2 size={14} className="animate-spin" /> : <Crown size={14} />}
              Upgrade to {(TIER_META[upgradeTo]?.label) || 'Pro'}
            </button>
          )}
          {isAdmin && !memberView && (
            <button
              type="button"
              onClick={() => setShowSettings((s) => !s)}
              data-testid="studio-limits-settings-toggle"
              className="inline-flex items-center gap-1.5 bg-white border border-[#E2E8F0] hover:border-[#7C3AED] text-[#475569] text-xs font-bold px-4 py-2 rounded-full transition-colors"
            >
              <Settings2 size={14} /> Plan limits
            </button>
          )}
        </div>
      </div>

      {showPlanDetails && <PlanDetails usage={usage} meta={meta} isAdmin={isAdmin} memberView={memberView} />}

      {isAdmin && !memberView && showSettings && (
        <AdminPlansEditor plans={usage.plans} features={usage.features} onSaved={load} />
      )}
    </div>
  );
}

function PlanRow({ label, value, locked }) {
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-[#F1F5F9] last:border-0" data-testid={`plan-detail-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <span className="text-sm text-[#475569]">{label}</span>
      <span className={`text-sm font-bold inline-flex items-center gap-1.5 ${locked ? 'text-[#94A3B8]' : 'text-[#0A0F1C]'}`}>
        {locked ? <Lock size={12} /> : <Check size={13} className="text-emerald-600" />}{value}
      </span>
    </div>
  );
}

function PlanDetails({ usage, meta, isAdmin, memberView = false }) {
  const p = usage.plan || {};
  const f = usage.features || {};
  const num = (v, suffix = '') => (v == null ? 'Unlimited' : `${v}${suffix}`);

  if (isAdmin) {
    return (
      <div className="mt-2 rounded-2xl border border-[#E9D5FF] bg-white p-5" data-testid="studio-plan-details">
        <p className="text-sm font-bold text-[#0A0F1C] mb-1">{memberView ? 'Unlimited access, no restrictions' : 'Admin access, no restrictions'}</p>
        <p className="text-sm text-[#64748B]">You have unlimited generations, unlimited brand profiles and reference uploads, content plans of any length, and no daily caps.</p>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-2xl border border-[#E9D5FF] bg-white p-5" data-testid="studio-plan-details">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-black uppercase tracking-wider" style={{ color: meta.color }}>{p.plan_name || meta.label} plan, what you can do</p>
        <span className="text-xs font-semibold text-[#64748B]">${(p.monthly_price ?? 0).toFixed(0)}/mo</span>
      </div>
      <div>
        <PlanRow label="Posts per day" value={num(p.posts_per_day)} />
        <PlanRow label="Short-form video scripts per day" value={num(p.videos_per_day)} />
        <PlanRow label="Content plan length" value={`Up to ${num(p.max_content_plan_days, ' days')}`} />
        <PlanRow label="Brand profiles" value={p.max_brand_profiles == null ? 'Unlimited' : `${p.max_brand_profiles}`} />
        <PlanRow label="Reference library uploads" value={p.max_library_uploads == null ? 'Unlimited' : `${p.max_library_uploads}`} />
        <PlanRow label="AI images per day" value={p.ai_images_per_day == null ? 'Unlimited' : `${p.ai_images_per_day}`} />
        <PlanRow label="AI videos per day" value={p.ai_videos_per_day == null ? 'Unlimited' : `${p.ai_videos_per_day}`} />
        <PlanRow label="Auto-scheduler" value={f.scheduler_enabled ? 'Included' : 'Coming soon'} locked={!f.scheduler_enabled} />
        <PlanRow label="Auto-publish to socials" value={f.auto_publish_enabled ? 'Included' : 'Coming soon'} locked={!f.auto_publish_enabled} />
      </div>
      <p className="text-[11px] text-[#94A3B8] mt-3">Daily post and video limits reset every morning. Need more? Upgrade any time, no contracts.</p>
    </div>
  );
}

function NumberField({ label, value, onChange, testid, allowNull = false, placeholder }) {
  const isUnlimited = value === null || value === undefined;
  return (
    <div className="flex items-center justify-between gap-2 text-sm">
      <span className="text-[#475569]">{label}</span>
      <div className="flex items-center gap-1.5">
        {allowNull && (
          <label className="flex items-center gap-1 text-[10px] font-bold uppercase text-[#7C3AED] cursor-pointer" title="Unlimited">
            <input type="checkbox" checked={isUnlimited} onChange={(e) => onChange(e.target.checked ? null : 0)} data-testid={`${testid}-unlimited`} />
            inf
          </label>
        )}
        <input
          type="number"
          min={0}
          max={100000}
          disabled={isUnlimited}
          value={isUnlimited ? '' : value}
          placeholder={placeholder || (isUnlimited ? 'unlimited' : '')}
          onChange={(e) => onChange(Math.max(0, parseInt(e.target.value || '0', 10)))}
          data-testid={testid}
          className="w-20 px-2 py-1.5 border border-[#E2E8F0] rounded-lg text-sm text-right focus:border-[#7C3AED] outline-none disabled:bg-slate-50 disabled:text-slate-400"
        />
      </div>
    </div>
  );
}

function PlanColumn({ tier, color, plan, setPlan, allowVisibility }) {
  const set = (k, v) => setPlan((p) => ({ ...p, [k]: v }));
  return (
    <div className="rounded-xl border bg-white p-4" style={{ borderColor: color + '40' }} data-testid={`plan-editor-${tier}`}>
      <p className="text-xs font-black uppercase tracking-wider mb-3" style={{ color }}>{plan.plan_name}</p>
      <div className="space-y-2">
        <NumberField label="Price ($/mo)" value={plan.monthly_price} onChange={(v) => set('monthly_price', v)} testid={`plan-${tier}-price`} />
        <NumberField label="Posts / day" value={plan.posts_per_day} onChange={(v) => set('posts_per_day', v)} testid={`plan-${tier}-posts`} />
        <NumberField label="Videos / day" value={plan.videos_per_day} onChange={(v) => set('videos_per_day', v)} testid={`plan-${tier}-videos`} />
        <NumberField label="Max plan days" value={plan.max_content_plan_days} onChange={(v) => set('max_content_plan_days', v)} testid={`plan-${tier}-days`} />
        <NumberField label="Brand profiles" value={plan.max_brand_profiles} onChange={(v) => set('max_brand_profiles', v)} testid={`plan-${tier}-brands`} allowNull />
        <NumberField label="Library uploads" value={plan.max_library_uploads} onChange={(v) => set('max_library_uploads', v)} testid={`plan-${tier}-uploads`} allowNull />
        <NumberField label="AI images / day" value={plan.ai_images_per_day} onChange={(v) => set('ai_images_per_day', v)} testid={`plan-${tier}-aiimages`} allowNull />
        <NumberField label="AI videos / day" value={plan.ai_videos_per_day} onChange={(v) => set('ai_videos_per_day', v)} testid={`plan-${tier}-aivideos`} allowNull />
        {allowVisibility && (
          <label className="flex items-center justify-between gap-2 text-sm pt-1">
            <span className="text-[#475569]">Public (sellable)</span>
            <input type="checkbox" checked={!!plan.is_public} onChange={(e) => set('is_public', e.target.checked)} data-testid={`plan-${tier}-public`} />
          </label>
        )}
      </div>
    </div>
  );
}

function AdminPlansEditor({ plans, features, onSaved }) {
  const [starter, setStarter] = useState(plans?.starter || {});
  const [pro, setPro] = useState(plans?.pro || {});
  const [premium, setPremium] = useState(plans?.premium || {});
  const [agency, setAgency] = useState(plans?.agency || {});
  const [feat, setFeat] = useState(features || {});
  const [saving, setSaving] = useState(false);

  const pack = (p) => ({
    monthly_price: Number(p.monthly_price) || 0,
    posts_per_day: Number(p.posts_per_day) || 0,
    videos_per_day: Number(p.videos_per_day) || 0,
    max_content_plan_days: Number(p.max_content_plan_days) || 30,
    max_brand_profiles: p.max_brand_profiles === null || p.max_brand_profiles === undefined ? null : Number(p.max_brand_profiles),
    max_library_uploads: p.max_library_uploads === null || p.max_library_uploads === undefined ? null : Number(p.max_library_uploads),
    ai_images_per_day: p.ai_images_per_day === null || p.ai_images_per_day === undefined ? null : Number(p.ai_images_per_day),
    ai_videos_per_day: p.ai_videos_per_day === null || p.ai_videos_per_day === undefined ? null : Number(p.ai_videos_per_day),
    is_public: !!p.is_public,
    is_active: p.is_active !== false,
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/api/admin/social-studio/plans', {
        starter: pack(starter), pro: pack(pro), premium: pack(premium), agency: pack(agency),
        features: {
          scheduler_enabled: !!feat.scheduler_enabled,
          auto_publish_enabled: !!feat.auto_publish_enabled,
          scheduler_access_level: feat.scheduler_access_level || 'premium',
          auto_publish_access_level: feat.auto_publish_access_level || 'premium',
        },
      });
      toast.success('Studio plans saved');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save plans');
    } finally {
      setSaving(false);
    }
  };

  const levelOptions = (
    <>
      <option value="starter">Starter+</option>
      <option value="pro">Pro+</option>
      <option value="premium">Premium+</option>
      <option value="agency">Agency only</option>
    </>
  );

  return (
    <div className="mt-3 rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-4" data-testid="studio-plans-editor">
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
        <PlanColumn tier="starter" color="#1D4ED8" plan={starter} setPlan={setStarter} />
        <PlanColumn tier="pro" color="#7C3AED" plan={pro} setPlan={setPro} />
        <PlanColumn tier="premium" color="#0EA5E9" plan={premium} setPlan={setPremium} />
        <PlanColumn tier="agency" color="#0F766E" plan={agency} setPlan={setAgency} allowVisibility />
      </div>

      <div className="mt-3 rounded-xl border border-[#E2E8F0] bg-white p-4" data-testid="studio-feature-toggles">
        <p className="text-xs font-black uppercase tracking-wider text-[#0A0F1C] mb-3">Scheduler & Auto-Publishing</p>
        <p className="text-[11px] text-[#64748B] mb-3">These are <strong>Coming Soon</strong>. Enable once functional. They become available to the selected tier and above.</p>
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <label className="flex items-center justify-between gap-2">
            <span className="text-[#475569]">Scheduler enabled</span>
            <input type="checkbox" checked={!!feat.scheduler_enabled} onChange={(e) => setFeat((f) => ({ ...f, scheduler_enabled: e.target.checked }))} data-testid="feature-scheduler-enabled" />
          </label>
          <label className="flex items-center justify-between gap-2">
            <span className="text-[#475569]">Scheduler unlocks at</span>
            <select value={feat.scheduler_access_level || 'premium'} onChange={(e) => setFeat((f) => ({ ...f, scheduler_access_level: e.target.value }))} data-testid="feature-scheduler-level" className="border border-[#E2E8F0] rounded-lg px-2 py-1 text-sm">
              {levelOptions}
            </select>
          </label>
          <label className="flex items-center justify-between gap-2">
            <span className="text-[#475569]">Auto-publish enabled</span>
            <input type="checkbox" checked={!!feat.auto_publish_enabled} onChange={(e) => setFeat((f) => ({ ...f, auto_publish_enabled: e.target.checked }))} data-testid="feature-autopublish-enabled" />
          </label>
          <label className="flex items-center justify-between gap-2">
            <span className="text-[#475569]">Auto-publish unlocks at</span>
            <select value={feat.auto_publish_access_level || 'premium'} onChange={(e) => setFeat((f) => ({ ...f, auto_publish_access_level: e.target.value }))} data-testid="feature-autopublish-level" className="border border-[#E2E8F0] rounded-lg px-2 py-1 text-sm">
              {levelOptions}
            </select>
          </label>
        </div>
      </div>

      <div className="mt-3 flex justify-end">
        <button
          type="button"
          onClick={save}
          disabled={saving}
          data-testid="studio-plans-save"
          className="inline-flex items-center gap-1.5 bg-[#0A0F1C] hover:bg-black text-white text-xs font-bold px-5 py-2.5 rounded-full transition-colors disabled:opacity-60"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save plans
        </button>
      </div>

      <WaitlistPanel />
      <RecoveryPanel />
    </div>
  );
}

function RecoveryPanel() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/api/admin/social-studio/checkout-recovery')
      .then((r) => setStats(r.data))
      .catch(() => setStats(false));
  }, []);

  if (stats === null || stats === false) return null;

  const Stat = ({ label, value, color }) => (
    <div className="rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] px-3 py-2 text-center" data-testid={`recovery-stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
      <p className="text-lg font-black" style={{ color: color || '#0A0F1C' }}>{value}</p>
      <p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">{label}</p>
    </div>
  );

  return (
    <div className="mt-4 rounded-xl border border-[#E9D5FF] bg-white p-4" data-testid="studio-recovery-panel">
      <div className="flex items-center gap-2 mb-3">
        <RefreshCw size={14} className="text-[#7C3AED]" />
        <p className="text-xs font-black uppercase tracking-wider text-[#7C3AED]">Abandoned checkout recovery</p>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3">
        <Stat label="Pending" value={stats.pending} color="#D97706" />
        <Stat label="Nudges sent" value={stats.nudges_sent} color="#7C3AED" />
        <Stat label="Recovered" value={stats.recovered_after_nudge} color="#059669" />
        <Stat label="Completed" value={stats.completed} color="#0A0F1C" />
        <Stat label="Expired" value={stats.expired} color="#94A3B8" />
      </div>
      {(!stats.recent || stats.recent.length === 0) ? (
        <p className="text-sm text-[#64748B]">No checkout starts yet. When a member begins a Studio checkout but does not pay, a one-time recovery email goes out automatically about an hour later.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto divide-y divide-[#F1F5F9]">
          {stats.recent.slice(0, 25).map((r) => (
            <div key={r.intent_id} className="py-2 flex items-center justify-between text-sm gap-2">
              <div className="min-w-0">
                <span className="font-semibold text-[#0A0F1C] truncate">{r.email}</span>
                <span className="text-[#64748B]"> · {(r.plan_name || r.tier)}</span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {r.recovery_sent && <span className="text-[10px] font-bold uppercase text-[#7C3AED]">nudged</span>}
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${r.status === 'completed' ? 'bg-emerald-50 text-emerald-700' : r.status === 'expired' ? 'bg-slate-100 text-slate-500' : 'bg-amber-50 text-amber-700'}`}>{r.status}</span>
                <span className="text-[11px] text-[#94A3B8]">{(r.created_at || '').slice(0, 10)}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function WaitlistPanel() {
  const [entries, setEntries] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get('/api/admin/social-studio/waitlist')
      .then((r) => setEntries(r.data?.entries || []))
      .catch(() => setEntries([]));
  }, []);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const r = await api.get('/api/admin/social-studio/waitlist/export', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'studio_waitlist.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not export the waitlist.');
    } finally {
      setDownloading(false);
    }
  };

  if (entries === null) return null;

  return (
    <div className="mt-4 rounded-xl border border-[#99F6E4] bg-white p-4" data-testid="studio-waitlist-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs font-black uppercase tracking-wider text-[#0F766E]">
          Agency waitlist <span className="text-[#94A3B8]">({entries.length})</span>
        </p>
        <button
          type="button"
          onClick={downloadCsv}
          disabled={downloading || entries.length === 0}
          data-testid="studio-waitlist-export"
          className="inline-flex items-center gap-1.5 bg-[#0F766E] hover:bg-[#0e6b63] text-white text-xs font-bold px-4 py-2 rounded-full transition-colors disabled:opacity-50"
        >
          {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} Download CSV
        </button>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-[#64748B]">No signups yet. They will appear here the moment someone joins the Agency waitlist.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto divide-y divide-[#F1F5F9]">
          {entries.slice(0, 50).map((e) => (
            <div key={e.email} className="py-2 flex items-center justify-between text-sm">
              <div>
                <span className="font-semibold text-[#0A0F1C]">{e.email}</span>
                {e.name && <span className="text-[#64748B]"> · {e.name}</span>}
              </div>
              <span className="text-[11px] text-[#94A3B8]">{(e.created_at || '').slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
